import { z } from "zod";
const digest = z.string().regex(/^[a-f0-9]{64}$/u);
const identifier = z.string().trim().min(1).max(200);
const offset = z.number().int().nonnegative().max(1_000_000);
/** Strongest first. A segment drawn from several sources takes the weakest of their tiers. */
export const runAnswerProvenanceTiers = ["governed", "curated_sql", "records", "web"];
export const runAnswerProvenanceTierSchema = z.enum([...runAnswerProvenanceTiers, "failed"]);
/**
 * A source the answer drew on, as metadata only: result rows stay behind the execution-result
 * route, which checks the reader's current source access.
 */
export const runAnswerProvenanceSourceSchema = z.object({
    id: identifier,
    kind: z.enum(["governed_product", "warehouse_query", "web_page", "tool_result"]),
    tier: z.enum(runAnswerProvenanceTiers),
    toolCallId: identifier,
    toolName: identifier,
    label: z.string().min(1).max(400),
    pluginSlug: identifier.optional(),
    productSlug: identifier.optional(),
    productVersion: z.number().int().positive().optional(),
    publicationVersionId: identifier.optional(),
    relations: z.array(z.string().min(1).max(260)).max(32).optional(),
    certificateId: identifier.optional(),
    observedAt: z.number().int().nonnegative().optional(),
    url: z.string().url().max(2_048).optional(),
    publishedDate: z.string().max(64).optional(),
}).strict();
const rangeSchema = z.object({ start: offset, end: offset }).strict();
/** What an answer did to figures it found to work out one it shows: `share` and `change` are percentages. */
export const runAnswerProvenanceOperations = ["sum", "difference", "product", "quotient", "reciprocal", "share", "change"];
/**
 * Where a figure was found in a governed product's aggregate result: the query, the row and the
 * metric. The records behind it are that query's filters and that row's group values.
 */
export const runAnswerProvenanceCellSchema = z.object({
    sourceId: identifier,
    queryId: identifier,
    row: z.number().int().nonnegative().max(10_000),
    column: identifier,
}).strict();
/**
 * A figure found in a result, or worked out from figures that were: its `derivation` names the
 * operation and the answer's own figures it took, and it names their sources. A figure found in
 * a governed aggregate names the `cells` it was found in.
 */
const claimSchema = z.object({
    start: offset,
    end: offset,
    sourceIds: z.array(identifier).min(1).max(16),
    derivation: z.object({
        operation: z.enum(runAnswerProvenanceOperations),
        operands: z.array(rangeSchema).min(1).max(2),
    }).strict().optional(),
    cells: z.array(runAnswerProvenanceCellSchema).min(1).max(8).optional(),
}).strict();
/** Offsets are UTF-16, end-exclusive, into the unchanged response text. */
export const runAnswerProvenanceSegmentSchema = z.object({
    start: offset,
    end: offset,
    tier: runAnswerProvenanceTierSchema,
    basis: z.enum(["verified", "matched"]),
    sourceIds: z.array(identifier).max(32),
    claims: z.array(claimSchema).max(64),
    unmatchedClaims: z.array(rangeSchema).max(64),
    reason: z.string().regex(/^[a-z_]+$/u).max(80).optional(),
}).strict().refine((segment) => segment.end > segment.start, "Segments must be non-empty.")
    .refine((segment) => segment.tier === "failed" || segment.sourceIds.length > 0, "Only a failed segment may name no source.");
/**
 * Where each part of a run's answer came from. `verified` segments come from server-checked
 * references; `matched` segments from the answer's figures, codes and links found in the
 * results the model saw. Text no segment covers has no recorded source. Provenance, not
 * correctness.
 */
export const runAnswerProvenanceSchema = z.object({
    schemaVersion: z.literal("mlx.run-answer-provenance.v1"),
    runId: identifier,
    answerSha256: digest,
    sources: z.array(runAnswerProvenanceSourceSchema).max(512),
    segments: z.array(runAnswerProvenanceSegmentSchema).max(1_024),
}).strict().superRefine((provenance, context) => {
    const ids = new Set(provenance.sources.map((source) => source.id));
    if (ids.size !== provenance.sources.length) {
        context.addIssue({ code: "custom", path: ["sources"], message: "Source ids must be unique." });
    }
    provenance.segments.forEach((segment, index) => {
        if (index > 0 && segment.start < provenance.segments[index - 1].end) {
            context.addIssue({ code: "custom", path: ["segments", index], message: "Segments must be ordered and nonoverlapping." });
        }
        const named = [...segment.sourceIds, ...segment.claims.flatMap((claim) => claim.sourceIds)];
        if (named.some((id) => !ids.has(id))) {
            context.addIssue({ code: "custom", path: ["segments", index], message: "Segments may name only listed sources." });
        }
    });
});
export const getTaskRunProvenanceParamsSchema = z.object({ runId: identifier }).strict();
